import React, { useEffect, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import MessageToster from "../component/MessageToster";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import { useRealAccounts } from "../hooks/useRealAccounts";
import { useAccountBalance } from "../hooks/useAccountBalance";
import { useUser } from "../context/UserContext";

// MT5 Account option in "Account Type". Set to false to allow Income Wallet withdrawals only.
const ENABLE_MT5_WITHDRAW = true;

export default function CryptoWithdraw() {
  const { userData, fetchUserData } = useUser();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);
  const { accounts } = useRealAccounts();

  // Withdraw networks come from /account/coin_list (one row per network)
  const [coins, setCoins] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState(null);

  const [formData, setFormData] = useState({
    withdraw_type: "COMMISSION",
    account_number: "",
    password: "",
    address: "",
    amount: ""
  });

  const [showPassword, setShowPassword] = useState(false);
  const { balance, loadingBalance, fetchBalance } = useAccountBalance(formData.withdraw_type === 'MT5' ? formData.account_number : "");
  const [formErrors, setFormErrors] = useState({});

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [timer, setTimer] = useState(120);
  const [, setCanResend] = useState(false);
  const [, setPaymentId] = useState("");
  const [, setWithdrawalId] = useState(0);
  const [, setWithdrawResponseData] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalPassword, setModalPassword] = useState("");
  const [modalPasswordError, setModalPasswordError] = useState("");
  // MT5 master password that was accepted by before_withdraw; reused for Resend OTP and the final withdraw call
  const [submittedPassword, setSubmittedPassword] = useState("");

  useEffect(() => {
    let interval;
    if (showOtpModal && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, timer]);

  useEffect(() => {
    fetchCoins();
  }, []);

  useEffect(() => {
    if (formData.withdraw_type === 'MT5' && accounts.length > 0 && !formData.account_number) {
      setFormData(prev => ({ ...prev, account_number: String(accounts[0]) }));
    }
  }, [formData.withdraw_type, accounts, formData.account_number]);

  useEffect(() => {
    if (otp.length === 6 && showOtpModal && !otpLoading) {
      const timer = setTimeout(() => {
        handleConfirmWithdraw();
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, showOtpModal]);

  const fetchCoins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/account/coin_list`, {
        headers: getApiHeaders()
      });
      const data = response.data?.data || response.data;
      // Only networks that are active and have withdraw enabled
      const withdrawableCoins = Array.isArray(data)
        ? data.filter((c) => c.is_withdraw_enabled && c.is_active)
        : [];
      setCoins(withdrawableCoins);
      setSelectedCoin(withdrawableCoins.length > 0 ? withdrawableCoins[0] : null);
    } catch (err) {
      console.error("Error fetching coin list:", err);
      handleError(err);
      setCoins([]);
      setSelectedCoin(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let { name, value, type } = e.target;
    if (type === 'number' && Number(value) < 0) {
      value = Math.abs(value).toString();
    }
    if (name === "amount") {
      value = String(value).replace(/[^0-9.]/g, "");
      if (value.includes("-") || value.toLowerCase().includes("e")) {
        return;
      }
    }
    if (name === "account_number") {
      value = value.replace(/\D/g, "");
    }
    if (name === "withdraw_type") {
      setFormData(prev => ({
        ...prev,
        withdraw_type: value,
        account_number: value === 'MT5' ? (accounts.length > 0 ? String(accounts[0]) : "") : ""
      }));
      setFormErrors(prev => ({ ...prev, [name]: "", account_number: "" }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateCryptoAddress = (address, network) => {
    if (!address) return false;
    const upperNetwork = String(network || "").toUpperCase();

    // TRC20: starts with 'T' and is 34 characters long (Base58)
    if (upperNetwork.includes("TRC20")) {
      const trc20Regex = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
      return trc20Regex.test(address);
    }

    // BEP20 / ERC20: starts with '0x' and is 42 characters long (Hex)
    if (upperNetwork.includes("BEP20") || upperNetwork.includes("ERC20")) {
      const ethRegex = /^0x[a-fA-F0-9]{40}$/;
      return ethRegex.test(address);
    }

    // Fallback basic check
    return address.length > 10;
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setModalPassword("");
    setModalPasswordError("");
  };

  // passwordValue: MT5 master password for MT5 withdrawals, "" for Income Wallet
  const executeBeforeWithdraw = async (passwordValue) => {
    if (!selectedCoin) {
      MessageToster("error", "Please select a network.");
      return;
    }

    setLoading(true);
    const amountNum = parseFloat(formData.amount);
    const addressStr = formData.address?.trim();
    const payload = {
      coin: selectedCoin.coin_symbol,
      cointype: selectedCoin.network_name,
      withdrawal_amount: amountNum,
      withdrawal_address: addressStr,
      withdraw_type: formData.withdraw_type,
      account_number: formData.withdraw_type === 'MT5' ? formData.account_number.trim() : null,
      password: passwordValue
    };

    try {
      const response = await axios.post(`${BASE_URL}/withdraw/before_withdraw_main`, payload, {
        headers: getApiHeaders()
      });

      if (response.data?.code === 200 || response.data?.status === "success" || response.data?.status === true) {
        MessageToster("success", response.data?.message || "OTP sent successfully.");
        setTimer(120);
        setCanResend(false);
        setOtp("");
        setPaymentId(response.data?.response?.paymentid || "");
        setWithdrawalId(response.data?.id || 0);
        setWithdrawResponseData(response.data);
        setShowPasswordModal(false);
        setModalPassword("");
        setShowOtpModal(true);
        setSubmittedPassword(passwordValue); // Save for resend OTP and the final withdraw call
      } else {
        MessageToster("error", response.data?.message || "Validation failed.");
      }
    } catch (error) {
      console.error("Before withdraw error:", error);
      if (error.response?.data?.message) {
        MessageToster("error", error.response.data.message);
      } else {
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCoin) {
      MessageToster("error", "Please select a network.");
      return;
    }

    const newErrors = {};
    const addressStr = formData.address?.trim();

    if (formData.withdraw_type === 'MT5' && !formData.account_number) {
      newErrors.account_number = "MT5 account not available.";
    }

    if (!addressStr) {
      newErrors.address = "Please enter a withdrawal address.";
    } else if (!validateCryptoAddress(addressStr, selectedCoin.network_name)) {
      newErrors.address = `Please enter a valid ${selectedCoin.network_name} address.`;
    }

    const amountNum = parseFloat(formData.amount);
    // Limits come from the selected network's coin_list row (never below $10)
    const effectiveMinWithdraw = Math.max(10, Number(selectedCoin.min_withdraw) || 0);
    const maxWithdraw = Number(selectedCoin.max_withdraw) || 0;

    if (!formData.amount) {
      newErrors.amount = "Please enter an amount.";
    } else if (isNaN(amountNum) || amountNum <= 0) {
      newErrors.amount = "Please enter a valid positive amount.";
    } else if (amountNum < effectiveMinWithdraw || (maxWithdraw > 0 && amountNum > maxWithdraw)) {
      newErrors.amount = maxWithdraw > 0
        ? `Amount must be between ${effectiveMinWithdraw} and ${maxWithdraw}.`
        : `Minimum withdrawal amount is $${effectiveMinWithdraw}.`;
    } else {
      // Check available balance (for MT5 only once the balance has loaded)
      const availableBalance = formData.withdraw_type === 'COMMISSION'
        ? (userData?.commissionwallet !== undefined ? Number(userData.commissionwallet) : null)
        : (balance !== null && !loadingBalance ? Number(balance) : null);

      if (availableBalance !== null && !isNaN(availableBalance) && amountNum > availableBalance) {
        newErrors.amount = `Insufficient balance. Available: $${availableBalance.toFixed(2)}`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    setFormErrors({});

    if (formData.withdraw_type === 'MT5') {
      setShowPasswordModal(true);
    } else {
      executeBeforeWithdraw("");
    }
  };

  const handleResendOtp = async () => {
    setOtp("");
    setTimer(120);
    setCanResend(false);

    const amountNum = parseFloat(formData.amount);
    const payload = {
      coin: selectedCoin?.coin_symbol || "",
      cointype: selectedCoin?.network_name || "",
      withdrawal_amount: amountNum,
      withdrawal_address: formData.address?.trim(),
      withdraw_type: formData.withdraw_type,
      account_number: formData.withdraw_type === 'MT5' ? formData.account_number.trim() : "",
      password: submittedPassword
    };

    try {
      const response = await axios.post(`${BASE_URL}/withdraw/before_withdraw_main`, payload, {
        headers: getApiHeaders()
      });

      if (response.data?.code === 200 || response.data?.status === "success" || response.data?.status === true) {
        MessageToster("success", response.data?.message || "OTP resent successfully.");
        setPaymentId(response.data?.response?.paymentid || "");
        setWithdrawalId(response.data?.id || 0);
        setWithdrawResponseData(response.data);
      } else {
        MessageToster("error", response.data?.message || "Failed to resend OTP.");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      if (error.response?.data?.message) {
        MessageToster("error", error.response.data.message);
      } else {
        handleError(error);
      }
    }
  };

  const handleConfirmWithdraw = async () => {
    if (!otp) {
      MessageToster("error", "Please enter the OTP.");
      return;
    }

    setOtpLoading(true);
    const payload = {
      coin: selectedCoin?.coin_symbol || "",
      cointype: selectedCoin?.network_name || "",
      withdrawal_amount: parseFloat(formData.amount),
      withdraw_type: formData.withdraw_type,
      otp: otp.toString(),
      account_number: formData.withdraw_type === 'MT5' ? (parseInt(formData.account_number, 10) || 0) : 0,
      password: submittedPassword,
      withdrawal_address: formData.address?.trim()
    };

    try {
      const response = await axios.post(`${BASE_URL}/withdraw/withdraw_main`, payload, {
        headers: getApiHeaders()
      });

      if (response.data?.code === 200 || response.data?.status === "success" || response.data?.status === true) {
        MessageToster("success", response.data?.message || "Withdrawal successful!");
        setShowOtpModal(false);
        if (fetchUserData) fetchUserData();
        if (fetchBalance) fetchBalance();
        navigate("/withdraw-history");
      } else {
        MessageToster("error", response.data?.message || "Withdrawal failed.");
      }
    } catch (error) {
      console.error("Withdraw error:", error);
      if (error.response?.data?.message) {
        MessageToster("error", error.response.data.message);
      } else {
        handleError(error);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const calculatedProfitAmount = balance !== null ? Math.max(0, Number(balance) - (userData?.Deposit_Amount !== undefined ? Number(userData.Deposit_Amount) : 0)).toFixed(2) : "0.00";

  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-0">Crypto Withdraw</h2>
                    <div className="page-title-right">
                      <button
                        className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                        onClick={() => navigate("/withdraw-history")}
                      >
                        <i className="bx bx-history me-2 fs-5"></i> Withdraw History
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div className="card shadow-sm border-0" style={{ borderRadius: "15px" }}>
                    <div className="card-body p-4">
                      {loading && coins.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status"></div>
                          <p className="mt-2 text-muted">Loading available networks...</p>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmit} noValidate>
                          {coins.length > 0 && (
                            <div className="mb-4">
                              <label className="form-label text-muted fw-semibold">Select Network</label>
                              <div className="row g-3">
                                {coins.map((coin) => {
                                  const isSelected = selectedCoin?.id === coin.id;
                                  return (
                                    <div className="col-md-6" key={coin.id}>
                                      <div
                                        className={`rounded-3 p-3 d-flex align-items-center gap-3 ${isSelected ? "bg-primary bg-opacity-10 border border-primary shadow-sm" : "bg-transparent border"}`}
                                        onClick={() => {
                                          setSelectedCoin(coin);
                                          setFormErrors((prev) => ({ ...prev, address: "", amount: "" }));
                                        }}
                                        style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                                      >
                                        <div className="avatar-sm flex-shrink-0">
                                          <span className={`avatar-title rounded-circle fs-4 ${isSelected ? "bg-primary text-white" : "bg-light text-primary"}`}>
                                            <i className="bx bx-wallet"></i>
                                          </span>
                                        </div>
                                        <div>
                                          <h6 className={`mb-1 fw-bold notranslate ${isSelected ? "text-primary" : ""}`} translate="no">
                                            {coin.coin_name} ({coin.coin_symbol})
                                          </h6>
                                          <span className="badge bg-primary-subtle text-primary notranslate" translate="no">
                                            {coin.network_name}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {selectedCoin ? (
                            <>
                              <div className="row">
                                <div className="col-md-6 mb-3">
                                  <div className="d-flex justify-content-between">
                                    <label className="form-label text-muted fw-semibold">Account Type</label>
                                  </div>
                                  <select
                                    className="form-select px-3 py-2 fw-medium shadow-none"
                                    name="withdraw_type"
                                    value={formData.withdraw_type}
                                    onChange={handleInputChange}
                                    style={{ cursor: 'pointer', borderRadius: "8px" }}
                                  >
                                    <option value="COMMISSION">INCOME WALLET</option>
                                    {/* Set ENABLE_MT5_WITHDRAW = false above to hide MT5 Account withdrawal */}
                                    {ENABLE_MT5_WITHDRAW && (
                                      <option value="MT5">
                                        MT5 Account {accounts.length > 0 ? `(${accounts[0]})` : ""}
                                      </option>
                                    )}
                                  </select>
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label text-muted fw-semibold">Available Balance</label>
                                  <div className="form-control px-3 py-2 fw-medium" style={{ cursor: 'not-allowed', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: "8px" }}>
                                    {formData.withdraw_type === 'COMMISSION' ? (
                                      <span>{`$${userData?.commissionwallet !== undefined ? userData.commissionwallet : '0.00'}`}</span>
                                    ) : (
                                      <span>{loadingBalance ? 'Loading...' : `$${balance !== null ? balance : '0.00'}`}</span>
                                    )}
                                  </div>
                                  {formErrors.account_number && (
                                    <small className="text-danger mt-1 d-block">{formErrors.account_number}</small>
                                  )}
                                </div>
                              </div>

                              {ENABLE_MT5_WITHDRAW && formData.withdraw_type === 'MT5' && (
                                <div className="row mb-3">
                                  <div className="col-md-6">
                                    <div className="card mb-3 mb-md-0 shadow-none border" style={{ borderRadius: '12px' }}>
                                      <div className="card-body p-3 d-flex align-items-center">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-primary-subtle" style={{ width: '48px', height: '48px' }}>
                                          <i className="bx bx-down-arrow-circle fs-3 text-primary"></i>
                                        </div>
                                        <div>
                                          <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Deposit Amount</p>
                                          <h5 className="mb-0 fw-bold">${userData?.Deposit_Amount !== undefined ? Number(userData.Deposit_Amount).toFixed(2) : "0.00"}</h5>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="card shadow-none border" style={{ borderRadius: '12px' }}>
                                      <div className="card-body p-3 d-flex align-items-center">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-primary-subtle" style={{ width: '48px', height: '48px' }}>
                                          <i className="bx bx-user-circle fs-3 text-primary"></i>
                                        </div>
                                        <div>
                                          <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Profit Amount</p>
                                          <h5 className="mb-0 fw-bold">${calculatedProfitAmount}</h5>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {(() => {
                                const effectiveMinWithdraw = Math.max(10, Number(selectedCoin.min_withdraw) || 0);
                                const amountNum = parseFloat(formData.amount) || 0;
                                // Withdraw charge % comes from get_user_data (withdraw_fee_per)
                                const rawFee = userData?.withdraw_fee_per ?? 0;
                                const feePercent = isNaN(parseFloat(rawFee)) ? 0 : parseFloat(rawFee);
                                const chargeAmount = (amountNum * feePercent) / 100;
                                const receivableAmount = Math.max(0, amountNum - chargeAmount);

                                return (
                                  <>
                                    <div className="row">
                                      <div className="col-md-6 mb-3">
                                        <label className="form-label text-muted fw-semibold">Withdrawal Address</label>
                                        <input
                                          type="text"
                                          className={`form-control px-3 py-2 ${formErrors.address ? 'is-invalid border-danger' : ''}`}
                                          placeholder={`Enter ${selectedCoin.network_name} address`}
                                          name="address"
                                          value={formData.address}
                                          onChange={handleInputChange}
                                          style={{ borderRadius: "8px" }}
                                          required
                                        />
                                        {formErrors.address ? (
                                          <small className="text-danger mt-1 d-block">{formErrors.address}</small>
                                        ) : (
                                          <small className="text-muted mt-1 d-block">
                                            <i className="bx bx-info-circle me-1"></i>
                                            Please ensure you enter a valid <span className="notranslate" translate="no">{selectedCoin.network_name}</span> address. Assets sent to the wrong network cannot be recovered.
                                          </small>
                                        )}
                                      </div>

                                      <div className="col-md-6 mb-3">
                                        <label className="form-label text-muted fw-semibold">Amount</label>
                                        <div className="input-group">
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            className={`form-control px-3 py-2 ${formErrors.amount ? 'is-invalid border-danger' : ''}`}
                                            placeholder={`Min Withdraw: $${effectiveMinWithdraw}`}
                                            name="amount"
                                            value={formData.amount}
                                            onKeyDown={(e) => {
                                              if (["e", "E", "+", "-"].includes(e.key)) {
                                                e.preventDefault();
                                              }
                                            }}
                                            onChange={handleInputChange}
                                            style={{ borderRadius: "8px 0 0 8px" }}
                                            required
                                          />
                                          <span className="input-group-text fw-bold notranslate" translate="no" style={{ borderRadius: "0 8px 8px 0" }}>{selectedCoin.coin_symbol}</span>
                                        </div>
                                        {formErrors.amount && (
                                          <small className="text-danger mt-1 d-block">{formErrors.amount}</small>
                                        )}
                                        <small className="text-muted mt-1 d-block">
                                          Min Withdraw: ${effectiveMinWithdraw}
                                        </small>
                                      </div>
                                    </div>

                                    <div className="row mb-4">
                                      <div className="col-md-6">
                                        <div className="card mb-3 mb-md-0 shadow-none border border-danger-subtle" style={{ borderRadius: '12px' }}>
                                          <div className="card-body p-3 d-flex align-items-center">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-danger-subtle" style={{ width: '48px', height: '48px' }}>
                                              <i className="bx bx-receipt fs-3 text-danger"></i>
                                            </div>
                                            <div>
                                              <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Charge ({feePercent}%)</p>
                                              <h5 className="text-danger mb-0 fw-bold notranslate" translate="no">
                                                <span>${chargeAmount.toFixed(2)}</span>
                                              </h5>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <div className="card shadow-none border border-success-subtle" style={{ borderRadius: '12px' }}>
                                          <div className="card-body p-3 d-flex align-items-center">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 bg-success-subtle" style={{ width: '48px', height: '48px' }}>
                                              <i className="bx bx-wallet-alt fs-3 text-success"></i>
                                            </div>
                                            <div>
                                              <p className="text-muted mb-1 fw-medium" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Receivable Amount</p>
                                              <h5 className="text-success mb-0 fw-bold notranslate" translate="no">
                                                <span>${receivableAmount.toFixed(2)}</span>
                                              </h5>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="text-end mt-4">
                                      <button type="submit" className="btn btn-primary px-4 py-2 fw-medium" disabled={loading}>
                                        {loading ? (
                                          <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span>
                                        ) : (
                                          <span>Submit Withdrawal</span>
                                        )}
                                      </button>
                                    </div>
                                  </>
                                );
                              })()}
                            </>
                          ) : (
                            !loading && (
                              <div className="alert alert-warning d-flex align-items-center mt-3" role="alert">
                                <i className="bx bx-info-circle fs-4 me-2"></i>
                                <div>No withdrawal networks available currently. Please contact support.</div>
                              </div>
                            )
                          )}
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* Password Modal Overlay (kept for MT5 if re-enabled) */}
      {showPasswordModal && (
        <>
          <div className="modal-backdrop fade show" style={{ backdropFilter: 'blur(4px)' }}></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <div className="modal-header border-bottom-0 pb-0 pt-4 px-4">
                  <h5 className="modal-title">Security Verification</h5>
                  <button type="button" className="btn-close" onClick={handleClosePasswordModal} disabled={loading}></button>
                </div>
                <div className="modal-body p-4 text-center">
                  <div className="mb-4 text-primary bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                    <i className="bx bx-lock-alt" style={{ fontSize: '48px' }}></i>
                  </div>
                  <p className="text-muted mb-4 px-3">
                    Please enter your MT5 Trading Password to confirm this withdrawal.
                  </p>

                  <div className="mb-4 text-start">
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={`form-control px-3 py-2 ${modalPasswordError ? 'is-invalid border-danger' : ''}`}
                        placeholder="Enter MT5 Trading Password"
                        value={modalPassword}
                        onChange={(e) => {
                          setModalPassword(e.target.value);
                          if (e.target.value) setModalPasswordError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (!modalPassword) setModalPasswordError("Please enter your MT5 Trading Password.");
                            else executeBeforeWithdraw(modalPassword);
                          }
                        }}
                        disabled={loading}
                      />
                      <button
                        className="btn btn-outline-secondary border"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={showPassword ? "mdi mdi-eye-outline" : "mdi mdi-eye-off-outline"} />
                      </button>
                    </div>
                    {modalPasswordError && (
                      <small className="text-danger mt-1 d-block">{modalPasswordError}</small>
                    )}
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary px-4 py-2" onClick={handleClosePasswordModal} disabled={loading}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4 py-2 fw-medium"
                      disabled={loading}
                      onClick={() => {
                        if (!modalPassword) {
                          setModalPasswordError("Please enter your MT5 Trading Password.");
                        } else {
                          executeBeforeWithdraw(modalPassword);
                        }
                      }}
                    >
                      {loading ? (
                        <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span>
                      ) : (
                        <span>Submit</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* OTP Modal Overlay */}
      {showOtpModal && (
        <>
          <div className="modal-backdrop fade show" style={{ backdropFilter: 'blur(4px)' }}></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <div className="modal-header border-bottom-0 pb-0 pt-4 px-4">
                  <h5 className="modal-title">Security Verification</h5>
                  <button type="button" className="btn-close" onClick={() => setShowOtpModal(false)} disabled={otpLoading}></button>
                </div>
                <div className="modal-body p-4 text-center">
                  <div className="mb-4 text-primary bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                    <i className="bx bx-shield-quarter" style={{ fontSize: '48px' }}></i>
                  </div>
                  <p className="text-muted mb-4 px-3">
                    Please enter the 6-digit verification code sent to your registered email or phone to securely confirm this withdrawal.
                  </p>

                  <div className="d-flex justify-content-center gap-2 mb-4">
                    {[0, 1, 2, 3, 4, 5].map((index) => {
                      return (
                        <input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="form-control text-center fw-bold text-primary shadow-sm"
                          style={{
                            width: "45px",
                            height: "55px",
                            fontSize: "24px",
                            borderRadius: "12px",
                            border: "2px solid #e0e0e0",
                            transition: "all 0.2s"
                          }}
                          value={otp[index] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!/^[0-9]*$/.test(val)) return;

                            let newOtpArray = otp.split("");
                            newOtpArray[index] = val.substring(val.length - 1);
                            setOtp(newOtpArray.join(""));

                            if (val && e.target.nextElementSibling) {
                              e.target.nextElementSibling.focus();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Backspace") {
                              if (!otp[index] && e.target.previousElementSibling) {
                                e.target.previousElementSibling.focus();
                              } else if (otp[index]) {
                                let newOtpArray = otp.split("");
                                newOtpArray[index] = "";
                                setOtp(newOtpArray.join(""));
                              }
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedData = e.clipboardData.getData("text/plain").trim();
                            if (!/^[0-9]+$/.test(pastedData)) return;

                            const pastedChars = pastedData.substring(0, 6).split("");
                            let newOtpArray = Array(6).fill("");

                            for (let i = 0; i < pastedChars.length; i++) {
                              newOtpArray[i] = pastedChars[i];
                            }
                            setOtp(newOtpArray.join(""));

                            const inputs = e.target.parentElement.querySelectorAll('input');
                            const targetIndex = Math.min(5, pastedChars.length);
                            if (inputs[targetIndex] && pastedChars.length < 6) {
                              inputs[targetIndex].focus();
                            } else if (inputs[5]) {
                              inputs[5].focus();
                            }
                          }}
                        />
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary btn-lg w-100 fw-bold rounded-3 shadow-sm mt-3"
                    disabled={otpLoading || otp.length !== 6}
                    onClick={handleConfirmWithdraw}
                  >
                    {otpLoading ? (
                      <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span>
                    ) : (
                      <span>Confirm Withdrawal</span>
                    )}
                  </button>

                  <div className="mt-3">
                    {timer > 0 ? (
                      <p className="text-muted mb-0">Resend OTP in <span className="fw-bold text-primary">{timer}s</span></p>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none fw-bold p-0 m-0"
                        onClick={handleResendOtp}
                        disabled={otpLoading}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
